import { useState } from 'react';

import { devWarn } from '@change/ds-utils';

/**
 * Function to provide the logic and checks of controlled and uncontrolled
 * components.
 * - Calls `useState` with `defaultValue`
 * - Warns in the console if both controlled and uncontrolled values are being used
 * - Returns a single value (controlled overwrites uncontrolled)
 * - Returns update function if uncontrolled
 *
 *  Controlled
 *
 * ```tsx
 * const { value } = useControlledOrUncontrolled({
 *   controlledValue: props.value
 * })
 *
 * <input value={value} />
 * ```
 *
 *  Uncontrolled
 *
 * ```tsx
 * const { value, setValue } = useControlledOrUncontrolled({
 *   defaultValue: ""
 * })
 * <input value={value} onChange={(ev) => setValue(ev.target.value)} />
 * ```
 *
 *  Indeterminate
 *
 * ```tsx
 * const { value, setValue } = useControlledOrUncontrolled({
 *   defaultValue: props.defaultValue
 *   controlledValue: props.value
 * })
 * <input value={value} onChange={(ev) => setValue.?(ev.target.value)} />
 * ```
 *
 * @returns - The value and an optional update function
 */
export function useControlledOrUncontrolled<T>({
	controlledValue,
	defaultValue,
	warnFn,
}: UseControlledOrUncontrolledParams<T>): [
	/**
	 * The current value, either controlled or uncontrolled
	 */
	value: T | undefined,
	/**
	 * Function to update the internal value on local state
	 *
	 * Will be undefined if a `controlledValue` is provided
	 * @param value - The new value
	 */
	setValue?: ((value: T) => void) | undefined,
] {
	const [localValue, setLocalValue] = useState<T | undefined>(defaultValue);

	if (controlledValue !== undefined && defaultValue !== undefined) {
		if (warnFn) {
			warnFn();
		} else {
			devWarn(
				'A controlled value and a default value were both provided. An element must be either controlled or uncontrolled. Decide between using any and remove on of these props',
			);
		}
	}

	return [controlledValue ?? localValue, controlledValue !== undefined ? undefined : setLocalValue];
}

export type UseControlledOrUncontrolledParams<T> = {
	/**
	 * The value controlled by a parent component
	 *
	 * Should not be used alongside `defaultValue`.
	 *
	 * If both are used, `controlledValue` will override `defaultValue`
	 * and the component will be controlled
	 */
	controlledValue?: T | undefined;
	/**
	 * The initial value of the internal state
	 *
	 * Should not be used alongside `controlledValue`
	 *
	 * If both are used, `controlledValue` will override `defaultValue`
	 * and the component will be controlled
	 */
	defaultValue?: T | undefined;
	/**
	 * A function to log a custom warning message in case
	 * `controlledValue` and `defaultValue` are both defined
	 */
	warnFn?: () => void;
};
